import { apiHandler } from '../api/apihandler';
import { DAILY_REPORTS_LIST, GAME_LIST } from '../api/constants';
import { fetchAllGameCategories } from './gameCategories';

// Every learning game a student has completed - used by the teacher's
// attendance Game Stats modal and the student's Learning Progress page
// (studentId optional there). student-games/completed-games isn't on the
// server, and the daily-reports `completed_games` list only covers part of
// it, so this walks every category's game list for the student - the same
// GAME_LIST + student_id call Learning Zone makes when a teacher plays for a
// student - and merges in the daily-reports list, one entry per game.
//
// Each entry is shaped like a daily-reports completed_games row:
// { id, game_name_id, game_name: { id, name, category: { id, name } }, level, score, created_at }

const isOk = (res) => res?.data?.response?.status === 200;

// Same completion rule as GamelevelItems: is_completed on the game, or any
// game_users row with is_complete = 1.
function toCompletedEntry(game, index, category) {
  const completedUser = Array.isArray(game?.game_users)
    ? game.game_users.find((u) => Number(u?.is_complete) === 1)
    : null;
  const isCompleted = !!completedUser || game?.is_completed === true || Number(game?.is_completed) === 1;
  if (!isCompleted) return null;

  return {
    id: `game-${game.id}`,
    game_name_id: game.id,
    game_name: {
      id: game.id,
      name: game?.name,
      is_ai_generated: game?.is_ai_generated,
      category: { id: category?.id, name: category?.name || '' },
    },
    level: completedUser?.level ?? game?.level ?? index + 1,
    score: completedUser?.score ?? game?.score ?? null,
    created_at: completedUser?.updated_at || completedUser?.created_at || null,
  };
}

async function fetchCategoryCompleted(category, studentId) {
  const res = await apiHandler(GAME_LIST, 'POST', {
    page: 1,
    limit: 100,
    category_id: category.id,
    ...(studentId ? { student_id: studentId } : {}),
  });
  if (!isOk(res)) return [];
  const games = res?.data?.data?.data?.data || [];
  return games.map((game, index) => toCompletedEntry(game, index, category)).filter(Boolean);
}

async function fetchDailyReportCompleted(studentId) {
  const res = await apiHandler(DAILY_REPORTS_LIST, 'POST', {
    page: 1,
    limit: 1,
    ...(studentId ? { student_id: studentId } : {}),
  });
  if (!isOk(res)) return [];
  const list = res?.data?.data?.completed_games;
  return Array.isArray(list) ? list : [];
}

// Resolves to { games, failed } - failed is true only when nothing could be
// loaded at all (so the caller can show an error instead of "none yet").
export async function fetchStudentCompletedGames(studentId) {
  const [categories, dailyList] = await Promise.all([
    fetchAllGameCategories().catch(() => null),
    fetchDailyReportCompleted(studentId).catch(() => []),
  ]);

  const perCategory = Array.isArray(categories)
    ? await Promise.all(categories.map((c) => fetchCategoryCompleted(c, studentId).catch(() => [])))
    : [];

  // Daily-reports rows first: they carry the real completion record (date,
  // score); the game-list rows only fill in games missing from it.
  const byGame = new Map();
  [...dailyList, ...perCategory.flat()].forEach((entry) => {
    const key = Number(entry?.game_name_id ?? entry?.game_name?.id);
    const mapKey = Number.isNaN(key) ? `x-${entry?.id}` : key;
    const existing = byGame.get(mapKey);
    if (!existing) {
      byGame.set(mapKey, entry);
      return;
    }
    // Keep the daily-reports record, but fill in the ids it may lack (needed
    // to open the game) from the game-list entry for the same game.
    const gameName = { ...(existing.game_name || {}) };
    const category = { ...(gameName.category || {}) };
    if (category.id == null && entry?.game_name?.category?.id != null) category.id = entry.game_name.category.id;
    if (!category.name && entry?.game_name?.category?.name) category.name = entry.game_name.category.name;
    if (gameName.id == null && entry?.game_name?.id != null) gameName.id = entry.game_name.id;
    if (gameName.is_ai_generated == null && entry?.game_name?.is_ai_generated != null) {
      gameName.is_ai_generated = entry.game_name.is_ai_generated;
    }
    byGame.set(mapKey, {
      ...existing,
      game_name_id: existing.game_name_id ?? entry?.game_name_id,
      game_name: { ...gameName, category },
    });
  });

  const games = [...byGame.values()].sort((a, b) => {
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });

  return { games, failed: categories === null && dailyList.length === 0 };
}
